import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  TextField,
  Typography,
  InputAdornment,
  Chip,
} from '@material-ui/core';
import { Edit, Search } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import Product from 'types/models/Product';
import PaginationData from 'types/models/PaginationData';
import UserRoleType from 'types/enums/UserRoleType';
import { getProducts, searchProducts } from 'services/productServices';
import { getProductsSheet } from 'services/productServices';
import EditCommerceContext from 'contexts/EditCommerceContext';
import AppContext from 'contexts/AppContext';
import MassiveLoad from 'components/ListProducts/MassiveLoad';
import BooleanIcon from 'components/common/BooleanIcon';
import { formatMoneyAuto } from 'utils/formatMoney';
import 'styles/pages/ListProductsPage.scss';

export default (): JSX.Element => {
  const history = useHistory();
  const [paginationProduct, setPaginationProduct] = React.useState<PaginationData<Product>>();
  const [searchProductText, setSearchProductText] = React.useState('');
  const [downloadingExcel, setDownloadingExcel] = React.useState<boolean | null>(null);

  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const { setSelectedProduct } = React.useContext(EditCommerceContext);
  const { selectedCommerce } = React.useContext(EditCommerceContext);
  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { t, i18n } = useTranslation();

  const loadProducts = React.useCallback(async (id: string, page?: number) => {
    const paginationData = await getProducts(id, page);
    if (paginationData) setPaginationProduct(paginationData);
  }, []);

  const downloadProductsSheet = async () => {
    try {
      setDownloadingExcel(true);
      const result = await getProductsSheet(i18n.language);
      const filename = i18n.language === 'es' ? 'productos.xlsx' : 'products.xlsx';
      if (result) {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(
          new Blob([result], {
            type: 'application/octet-stream',
          }),
        );
        a.download = filename;
        a.click();
        a.remove();
      }
    } finally {
      setDownloadingExcel(false);
    }
  };

  React.useEffect(() => {
    if (currentPageListNumber && selectedCommerce) {
      loadProducts(selectedCommerce.id, currentPageListNumber);
    } else {
      history.push('/private/commerces');
    }
  }, [currentPageListNumber]);

  const handleSearchTerm = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchProductText(event.target.value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      loadSearchData(selectedCommerce?.id ?? '', event.target.value);
    }, 500);
  };

  const loadSearchData = React.useCallback(async (commerceId: string, productSearchTerm: string, page?: number) => {
    const paginationData: PaginationData<Product> | null = await searchProducts(commerceId, productSearchTerm, page);
    if (paginationData) {
      setPaginationProduct(paginationData);
    }
  }, []);

  React.useEffect(() => {
    if (!searchProductText && !paginationProduct) return;
    if (!searchProductText && paginationProduct) {
      if (selectedCommerce) {
        loadProducts(selectedCommerce?.id);
      }
    }
  }, [searchProductText]);

  const renderEditButton = (product: Product): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedProduct(product);
          history.push('products/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: Product[]) => {
    return (
      <>
        {paginationData.map((product: Product) => (
          <TableRow key={product.id}>
            <TableCell component="th" scope="row">
              {product.name}
            </TableCell>
            <TableCell>{product.description}</TableCell>
            <TableCell>{formatMoneyAuto(product.price)}</TableCell>
            <TableCell>
              <BooleanIcon value={product.isPromotion} showText />
            </TableCell>
            <TableCell>
              <BooleanIcon value={product.enabled} />
            </TableCell>
            <TableCell className="lowercase-value-cell">{`${
              product.estimatedPreparationTime
                ? `${product.estimatedPreparationTime} ${t('listProduct-page-minutes', {
                    plural: product.estimatedPreparationTime > 1 ? 's' : '',
                  })}`
                : '-'
            }`}</TableCell>
            <TableCell>
              <BooleanIcon value={product.isUsedForTomorrowBenefit} showText />
            </TableCell>
            <TableCell>
              {product.category?.description ? (
                <Chip size="small" variant="outlined" label={product.category?.description} />
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>{renderEditButton(product)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <div className="search-container">
        <TextField
          id="search"
          name="searchValue"
          variant="outlined"
          label={t('listProduct-page-search-placeholder')}
          value={searchProductText}
          onChange={handleSearchTerm}
          margin="dense"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            style: {
              borderRadius: '10px',
              backgroundColor: '#fff',
            },
          }}
        />
      </div>
      <Button
        className="btn-50"
        variant="contained"
        color="primary"
        onClick={() => {
          history.push('products/create');
        }}
      >
        {t('listProduct-page-create')}
      </Button>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listProduct-page-name')}</TableCell>
              <TableCell>{t('listProduct-page-description')}</TableCell>
              <TableCell>{t('listProduct-page-price')}</TableCell>
              <TableCell>{t('listProduct-page-isPromotion')}</TableCell>
              <TableCell>{t('listProduct-page-enabled')}</TableCell>
              <TableCell>{t('listProduct-page-timeOfPreparation')}</TableCell>
              <TableCell>{t('listProduct-page-benefit-enabled')}</TableCell>
              <TableCell>{t('listProduct-page-category')}</TableCell>
              <TableCell>{t('listProduct-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationProduct?.data && paginationProduct?.data.length > 0 ? (
              renderContent(paginationProduct.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listProduct-page-noProducts')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationProduct && (
        <TablePagination
          component="div"
          count={paginationProduct.total}
          page={paginationProduct.currentPage - 1}
          onPageChange={(_, page: number) => {
            if (selectedCommerce) {
              setCurrentPageListNumber(page + 1);
            }
          }}
          rowsPerPage={paginationProduct.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}

      {(authData?.user?.role === UserRoleType.VenueEditor || authData?.user?.role === UserRoleType.CommerceEditor) && (
        <div className="massive-load-container">
          {downloadingExcel && <Typography color="primary">{t('loading')}</Typography>}

          <Button fullWidth color="default" variant="contained" onClick={downloadProductsSheet}>
            {t('listProduct-page-download-excel')}
          </Button>

          <MassiveLoad onSuccess={() => selectedCommerce && loadProducts(selectedCommerce.id)} />
        </div>
      )}
    </>
  );
};
